import { Observable, filter, first, switchMap, map, tap, firstValueFrom, shareReplay } from 'rxjs';
import { NgModule, APP_INITIALIZER, ErrorHandler, Injector } from '@angular/core';
import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { RouterModule, UrlSerializer } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { ReactiveFormsModule } from '@angular/forms';
import { initSentry } from '../../sentry.client.config';

// HTTP INTERCEPTORS  //
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { HttpLoggerInterceptor } from './api/http-logger.interceptor';
import { HttpErrorsInterceptor } from './api/http-errors.interceptor';
import { HttpHeadersInterceptor } from './api/http-header.interceptor';
import { HtmlDecodeInterceptor } from './api/html-decode.interceptor';

// APP //
import { environment } from '../environments/environment';
import { OldRoutesRoutingModule } from './old-routes-routing.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

// MODULES //
import { CoreModule } from './core/core.module';
import { AuthModule } from './auth/auth.module';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { MineosChatbotModule } from './mineos-chatbot/mineos-chatbot.module';
import { ContentPipeModule } from './services/content/content-pipe.module';
import { CsvConvertorModule } from './csv-convertor/csv-convertor.module';
import { E2EModule } from '../../cypress/support/e2e.module';

// SHARED //
import { ConvertPiiTypeToDataTypePipe } from './shared/pipes/convert-pii-type-to-data-type.pipe';
import { LowerCaseFirstLetterPipe } from './shared/pipes/lower-case-first-letter.pipe';
import { MineSnackbarComponent } from './shared/mine-snackbar/mine-snackbar.component';
import { CamelCaseWithSpacesPipe } from './shared/pipes/camel-case-with-spaces.pipe';
import { SplitUpperCasePipe } from './shared/pipes/split-upper-case.pipe';
import { SentenceCasePipe } from './shared/pipes/sentence-case.pipe';
import { HtmlDecodePipe } from './shared/pipes/html-decode.pipe';

// SERVICES //
import { JL } from 'jsnlog';
import { AkitaNgDevtools } from '@datorama/akita-ngdevtools';
import { RoleEnum } from './core/models/permission-role.enum';
import { RolePermissionsHelper } from './auth/role-permmissions-helper';
import { BootstrapService } from './services/bootstrap.service';
import { CustomFieldsBootstrapService } from './services/custom-fields-bootstrap.service';
import { GlobalErrorHandler } from './services/global-error-handler';
import { FeatureFlagQuery } from './feature-flag/state/feature-flag.query';
import { FeatureFlags } from './api/models/profile/profile-feature-flags.enum';
import { CustomUrlSerializer } from './services/custom-url-serializer.service';
import { CompanySettingsService } from './company-settings/state/company-settings.service';
import { FrameworksService } from './company-settings/state/frameworks/frameworks.service';
import { CustomViewsService } from './custom-views/state/custom-views.service';
import { DataTypesService } from './data-types/services/data-types.service';
import { ProfileService } from './profile/state/profile.service';
import { PermissionsService } from './auth/permissions.service';
import { PusherService } from './pusher/pusher.service';
import { ProfileQuery } from './profile/state/profile.query';
import { ChatService } from './chat/chat.service';
import { AuthService } from './auth/auth.service';
import { DsrPrivacyRightsService } from "./dsr/dsr-setup/dsr-privacy-rights/services/dsr-privacy-rights.service";
import { IntegrationIpaasService } from "./systems/services/integration-ipaas.service";
import { RegionsService } from './services/regions/regions.service';
import { LocaleCodeToDisplayNamePipe } from './shared/pipes/locale-code-to-display-name.pipe';
import { ProfileFeatureFlags } from "./api/models/profile/profile-feature-flags";
import { DsrTemplatesService } from "./dsr/dsr-setup/dsr-templates/services/dsr-templates.service";
import { FEATURE_FLAGS_API_SERVICE } from './feature-flag/feature-flag-injection-token';
import { USER_PROPERTIES_SERVICE } from './analytics/user-properties-injection-token';
import { ApiClientFeaturesService } from './api/api-client-features.service';
import { UserPropertiesService } from './analytics/user-properties.service';
import { ApiClientSystemsService } from './api/api-client-systems.service';
import { API_CLIENT_COMPANY_SETTINGS_SERVICE } from './api/injectors/api-client-company-settings.token';
import { ApiClientCompanySettingsService } from './api/api-client-company-settings.service';
import { API_CLIENT_SYSTEMS_SERVICE } from './api/injectors/api-client-systems-service.token';
import { ApiClientAssessmentsService } from './api/api-client-ai-assessments.service';
import { API_CLIENT_ASSESSMENTS_SERVICE } from './api/injectors/api-client-ai-assessments.token';
import { API_CLIENT_RISKS_SERVICE } from './api/injectors/api-client-risks.token';
import { API_CLIENT_LABELS_SERVICE } from './api/injectors/api-client-labels.token';
import { ApiClientRisksService } from './api/api-client-risks.service';
import { API_CLIENT_EMPLOYEES_SERVICE } from './api/injectors/api-client-employees.token';
import { ApiClientEmployeesService } from './api/api-client-employees.service';
import { API_CLIENT_CUSTOM_FIELDS_V2_SERVICE } from './api/injectors/api-client-custom-fields-v2.token';
import { ApiClientCustomFieldsV2Service } from './api/api-client-custom-fields-v2.service';
import { GoogleCopilotComponent } from './shared/google-copilot/google-copilot.component';
import { ApiClientLabelsService } from './api/api-client-labels.service';
import { RandomUUIDPipe } from './shared/pipes/random-uuid.pipe';
import { NormalizeKeyPipe } from './shared/pipes/normalize-key.pipe';
import { CustomEntityChipBackgroundColorPipe } from './ai-assessments/pipes/custom-entity-chip-color.pipe';
import { LoggerService } from './logger/logger.service';
import { DsrMultiBrandService } from './dsr/dsr-setup/dsr-multi-brand/services/dsr-multi-brand.service';

function initApp(bootstrapService: BootstrapService): () => Observable<void> {
	return () => bootstrapService.preLoad();
}

function initCustomFieldsBootstrap(service: CustomFieldsBootstrapService): () => void {
	return () => service.init();
}

const pipes = [
	DatePipe,
	TitleCasePipe,
	HtmlDecodePipe,
	SentenceCasePipe,
	SplitUpperCasePipe,
	CamelCaseWithSpacesPipe,
	LowerCaseFirstLetterPipe,
	ConvertPiiTypeToDataTypePipe,
	LocaleCodeToDisplayNamePipe,
	CustomEntityChipBackgroundColorPipe,
	RandomUUIDPipe,
	NormalizeKeyPipe
];

@NgModule({
	declarations: [
		AppComponent,
	],
	imports: [
		[...pipes],
		CoreModule,
		CommonModule,
		BrowserModule,
		ReactiveFormsModule,
		ContentPipeModule,
		RouterModule,
		AuthModule,
		MineSnackbarComponent,
		OldRoutesRoutingModule,
		CsvConvertorModule,
		MonacoEditorModule.forRoot({
			baseUrl: environment.assets.url + "/Portal/packages/monaco-editor/v0-52-2/monaco/min/vs" 
		}),
		environment.production ? [] : AkitaNgDevtools.forRoot(),
		MineosChatbotModule,
		E2EModule,
		GoogleCopilotComponent,
		AppRoutingModule,
	],
	providers: [
		[...pipes],
		{ provide: 'JSNLOG', useValue: JL },
		{ provide: APP_INITIALIZER, useFactory: initApp, deps: [BootstrapService], multi: true },
		{ provide: APP_INITIALIZER, useFactory: initCustomFieldsBootstrap, deps: [CustomFieldsBootstrapService], multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: HttpLoggerInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: HttpErrorsInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: HttpHeadersInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: HtmlDecodeInterceptor, multi: true },
		{ provide: FEATURE_FLAGS_API_SERVICE, useClass: ApiClientFeaturesService },
		{ provide: USER_PROPERTIES_SERVICE, useClass: UserPropertiesService },
		{ provide: API_CLIENT_SYSTEMS_SERVICE, useClass: ApiClientSystemsService },
		{ provide: API_CLIENT_COMPANY_SETTINGS_SERVICE, useClass: ApiClientCompanySettingsService },
		{ provide: API_CLIENT_ASSESSMENTS_SERVICE, useClass: ApiClientAssessmentsService },
		{ provide: API_CLIENT_RISKS_SERVICE, useClass: ApiClientRisksService },
		{ provide: API_CLIENT_LABELS_SERVICE, useClass: ApiClientLabelsService },
		{ provide: API_CLIENT_EMPLOYEES_SERVICE, useClass: ApiClientEmployeesService },
		{ provide: API_CLIENT_CUSTOM_FIELDS_V2_SERVICE, useClass: ApiClientCustomFieldsV2Service },
		{ provide: ErrorHandler, useClass: GlobalErrorHandler, },
		{ provide: UrlSerializer, useClass: CustomUrlSerializer },

	],
	bootstrap: [AppComponent]
})
export class AppModule {
	static injector: Injector;

	constructor (injector: Injector) {
		AppModule.injector = injector;
		return initModule(injector);
	}
}
export async function initModule(injector: Injector): Promise<void> {
	const authService = injector.get(AuthService);
	const featureFlagQuery = injector.get(FeatureFlagQuery);
	const permissionsService = injector.get(PermissionsService);
	const regionsService = injector.get(RegionsService);
	const loggerService = injector.get(LoggerService);

	try {
		const isLoggedIn$ = authService.isLoggedIn$.pipe(
			filter(res => res),
		);

		await firstValueFrom(isLoggedIn$);

		await firstValueFrom(injector.get(ProfileService).getUserProfile());

        const featureFlagsLoading$ = featureFlagQuery.selectLoading().pipe(filter(loading => !loading), shareReplay());

		const sentryFlag$ = featureFlagQuery.selectFlag(FeatureFlags.SentryRecordReplays).pipe(
			first(),
			tap(sentryRecordReplays => {
				loggerService.info('AppModule', 'Sentry is enabled');
				const environmentName = environment.sentry.environment || (environment.production ? 'production' : 'staging');
				initSentry(environment.sentry.dsn, environmentName, sentryRecordReplays);
			}),
		);
		
		await firstValueFrom(featureFlagsLoading$.pipe(
			switchMap(() => sentryFlag$),
		));

		const chatService$ = featureFlagsLoading$.pipe(
			switchMap(() => featureFlagQuery.selectFlag(FeatureFlags.DevMineOSChatbot)),
			first(),
			map(flag => flag ? injector.get(PusherService).init(`private-${injector.get(ProfileQuery).getCompanyId()}`) : injector.get(ChatService).init()),
		);

		let apis: Promise<void | boolean | Observable<void | boolean>>[] = [firstValueFrom(chatService$)];
		const userRole = await firstValueFrom(injector.get(ProfileQuery).selectAgentRole());
		
		if (!!userRole && userRole !== RoleEnum.Unknown) {
			apis = apis.concat(firstValueFrom(injector.get(DataTypesService).init()));
			const selectCustomViewsAllowed = permissionsService.selectIsAllowed([RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrAgent, RoleEnum.DsrManager, RoleEnum.InventoryManager]);
			const selectCompanySettingsAllowed = permissionsService.selectIsAllowed([RoleEnum.Admin, RoleEnum.Agent, RoleEnum.DsrManager, RoleEnum.InventoryManager]);
			const isCustomViewsPermitted = await firstValueFrom(selectCustomViewsAllowed);
			const isCompanySettingsPermitted = await firstValueFrom(selectCompanySettingsAllowed);
			if (isCustomViewsPermitted) {
				apis = apis.concat([
					firstValueFrom(injector.get(CustomViewsService).init()),
				]);
			}

			if (isCompanySettingsPermitted) {
				apis = apis.concat([
					firstValueFrom(injector.get(CompanySettingsService).init()),
				]);
			}            
			const privacyRightsRoles = RolePermissionsHelper.getPerrmitedRoleForPrivacyRights();
			const isPrivacyRightsPermitted = await firstValueFrom(permissionsService.selectIsAllowed(privacyRightsRoles));
			// DSR tasks (DevDsrCollaboratorTasks): the Reviewer is the DSR collaborator and needs the rights list
			// for the task panel (right name/description/processing type) — BE opens `dsr/v1/rights/list` to Reviewer.
			const dsrTasksFlag$ = featureFlagsLoading$.pipe(switchMap(() => featureFlagQuery.selectFlag(FeatureFlags.DevDsrCollaboratorTasks)), first());
			const isReviewerDsrTasks = await firstValueFrom(dsrTasksFlag$) && await firstValueFrom(permissionsService.selectIsAllowed([RoleEnum.Reviewer]));
			if (isPrivacyRightsPermitted || isReviewerDsrTasks) {
                const privacyRightsData$ = injector.get(DsrPrivacyRightsService).initRights();
                apis = apis.concat(firstValueFrom(privacyRightsData$));
            }

			const brandsDataRoles = RolePermissionsHelper.getPerrmitedRoleForBrands();
			const isBrandsDataPermitted = await firstValueFrom(permissionsService.selectIsAllowed(brandsDataRoles));
			const multiDataPromises$ = featureFlagsLoading$.pipe(
                switchMap(() => featureFlagQuery.selectMultipleFlags([FeatureFlags.IPaasIntegration])),
                first(),
                map(flags => initMultiDataByFFs(flags, isBrandsDataPermitted)),
            );

            const multiDataPromises = await firstValueFrom(multiDataPromises$);
            apis = apis.concat(multiDataPromises);
			apis = apis.concat(firstValueFrom(regionsService.getRegions()));
			
			const dsrTemplatesRoles = RolePermissionsHelper.getPerrmitedRoleForDsrTemplates();
            const isTemplatesPermitted = await firstValueFrom(permissionsService.selectIsAllowed(dsrTemplatesRoles));
			if (isTemplatesPermitted) {
				AppModule.injector.get(DsrTemplatesService).initData();
			}

			await Promise.all(apis);
			
			if (isCustomViewsPermitted) {
				await firstValueFrom(injector.get(FrameworksService).init());
			}
		}
		else {
			await Promise.all(apis);
		}
	}
	catch (e) {
		console.error(e);
	}
}

function initMultiDataByFFs(flags: Partial<ProfileFeatureFlags>, isBrandsPermitted: boolean): Promise<void>[] {
	const promises = [];
		if (flags[FeatureFlags.IPaasIntegration]) {
			promises.push(Promise.resolve(AppModule.injector.get(IntegrationIpaasService).init()));
		}
		if (isBrandsPermitted) {
			promises.push(firstValueFrom(AppModule.injector.get(DsrMultiBrandService).initBrands()));
		}
	return promises;
}